import { dataService } from '@app/modules/data';
import { DataObject, ObjectOptions } from './base';

import { TicketChange } from "./ticketchange";

/*******************************/
/* TICKETSII INSTANCE          */
/*******************************/

export interface _TicketSII {
    change: number,
    region: string,
    sent: Date,
    valid: string,
    error: string
};

interface _TicketSIIData extends _TicketSII {
    objid?: number;
    _uuid?: string;
    created?: Date;
    updated?: Date;
};

abstract class TicketSIIData extends DataObject {
    protected _ticketsii: _TicketSIIData = {
        change: null,
        region: null,
        sent: null,
        valid: null,
        error: null
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);

        this._ticketsii.created = new Date();
        this._ticketsii.updated = new Date();
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get created(){
        return this._ticketsii.created;
    }

    get updated(){
        return this._ticketsii.updated;        
    }

    get change(): TicketChange {
        return this._children['change'] || null;
    }

    set change(value: TicketChange){
        if (this.SetChild('change', value, 'change')){
            this.ToUpdate = true;
        }
    }

    /* read only properties */    

    get region(){
        return this._ticketsii.region;
    }

    get sent(){
        return this._ticketsii.sent;
    }

    get valid(){
        return this._ticketsii.valid;
    }

    get error(){
        return this._ticketsii.error;
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return {
            tckchg: this._ticketsii.change
        };
    }

    protected get Depend() {
        return {
            change: { item: this.change, relation_info: { to: 'ticketsii', by: 'change' } },    // this[by -> 'change'][to -> 'ticketsii'] => this
        };
    }

    protected get Children(){
        return [];
    }


    /****************************/
    /* DATA OBJECT              */
    /****************************/
    
    private _patchData(_ticketsii: _TicketSII){
        let _toUpdate = false;

        _toUpdate = this.patchValue(this._ticketsii, 'change', _ticketsii['change']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticketsii, 'region', _ticketsii['region']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticketsii, 'sent', _ticketsii['sent']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticketsii, 'valid', _ticketsii['valid']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticketsii, 'error', _ticketsii['error']) || _toUpdate;

        return _toUpdate;
    }

    set Data(_ticketsii: _TicketSII){
        this.patchValue(this._ticketsii, 'created', _ticketsii['created']);
        this.patchValue(this._ticketsii, 'updated', _ticketsii['updated']);
        
        if (this._patchData(_ticketsii)){
            this.ToUpdate = true;
        }
    }

    get Info(){
        return this._ticketsii;
    }

    set Info(value){
        this.DoPatchValues(value);
    }
   
    private DoPatchValues(_ticketsii: _TicketSII){
        this._patchData(_ticketsii);

        if (_ticketsii['change']){    // update children: 'change'
            let _objid = _ticketsii['objid'].toString();
            this.SetChild('change', new TicketChange(_objid, this.data, this._objoptions), 'change')
        }
        else {
            this.SetChild('change', null, 'change');
        }
    }

    private _ddbb(info): _TicketSIIData {
        let _ticketsii: _TicketSIIData = {
            objid: info['objid'] ? parseInt(info['objid']) : null,
            created: new Date(Date.parse(this.mysqlToDateStr(info['created']))),
            updated: new Date(Date.parse(this.mysqlToDateStr(info['updated']))),
            change: info['tckchg'] ? parseInt(info['tckchg']) : null,
            region: info['region'],
            sent: info['sent'] ? new Date(Date.parse(this.mysqlToDateStr(info['sent']))) : null,
            valid: info['valid'],
            error: info['error']
        };
        return _ticketsii;
    }

    protected _OnUpdate(info){
        let _ticketsii = this._ddbb(info);
        this.patchValue(this._ticketsii, 'objid', _ticketsii['objid']);
        this.patchValue(this._ticketsii, 'created', _ticketsii['created']);
        this.patchValue(this._ticketsii, 'updated', _ticketsii['updated']);
        this.DoPatchValues(_ticketsii);
    }
}

export class TicketSII extends TicketSIIData {
    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null){
        super('TICKETSII', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []): TicketSII {
        return this._Copy(store) as TicketSII;
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/
    
    // OK / KO / PENDING / SKIP distinction.
    //
    // The server may store:
    //   valid != null && !SKIP         -> OK (server accepted it)
    //   valid LIKE 'SKIP%'             -> SKIP (intentionally not sent)
    //   valid == null && error != null -> KO (server rejected it)
    //   valid == null && error == null -> PENDING (async hasn't sent yet)
    //
    // IsValid was previously a single boolean that conflated SKIP with OK and
    // PENDING with KO; consumers should now check IsOK / IsKO / IsPending /
    // IsSkip explicitly. IsValid is preserved for backwards compatibility but
    // is equivalent to "valid is set" (i.e. OK or SKIP).
    get IsValid(){
        return (this.valid != null);
    }

    get IsSkip(){
        return (typeof this.valid === 'string') && this.valid.startsWith('SKIP');
    }

    get IsOK(){
        return (this.valid != null) && !this.IsSkip;
    }

    get IsKO(){
        return !this.IsOK && !this.IsSkip && (this.error != null);
    }

    get IsPending(){
        return !this.IsOK && !this.IsSkip && !this.IsKO;
    }
}

